// ============================================================================
// 类型定义
// ============================================================================

/** 环境类型 */
export type Environment = "development" | "test" | "production" | "local";

/** 应用类型 */
export type AppType = "admin" | "tr" | "h5" | "c";

/** 布局类型（用于 SSO 登录页） */
export type LayoutType = "web" | "pad" | "mobile";

/** 域名来源 */
export type DomainSource = "domain" | "callback";

/** 品牌配置接口 */
export interface BrandConfig {
  /** 品牌 Logo 路径（将在运行时从 @ifa/ui 获取实际资源） */
  logo: string;
  /** 侧边栏纯色 Logo 路径（将在运行时从 @ifa/ui 获取实际资源） */
  solidLogo: string;
  /** 品牌名称 */
  name: string;
  /** 系统名称 */
  systemName: string;
  /** Favicon 路径（将在运行时从 @ifa/ui 获取实际资源） */
  favicon: string;
  /** 版权信息 */
  copyright: string;
}

/** 域名配置接口 */
interface DomainConfig {
  /** 域名匹配模式（支持字符串或正则表达式） */
  pattern: string | RegExp;
  /** API 基础地址 */
  apiBaseUrl: string;
  /** SSO 登录页地址 */
  authUrl: string;
  /** 应用类型（可选） */
  appType?: AppType;
  /** 是否为本地开发环境 */
  isLocal?: boolean;
}

/** 租户完整配置接口 */
interface TenantFullConfig {
  /** 租户编码 */
  tenantCode: string;
  /** 租户显示名称 */
  displayName: string;
  /** 品牌配置 */
  brand: BrandConfig;
  /** 域名配置列表 */
  domains: DomainConfig[];
  /** SSO 配置 */
  sso: {
    /** SSO 登录页域名 */
    hostname: string;
    /** 是否需要用户手动输入租户码（user@tenant-code 格式） */
    requireManualTenantInput: boolean;
  };
}

/** 租户配置接口（对外暴露） */
export interface TenantConfig {
  /** 域名（不包含协议，如：sso.cloud.bibl.com.hk） */
  hostname: string;
  /** 租户编码 */
  tenantCode: string;
  /** 是否需要用户手动输入租户码（user@tenant-code 格式） */
  requireManualTenantInput: boolean;
  /** 租户显示名称（可选） */
  displayName?: string;
}

/** 环境配置接口 */
export interface EnvConfig {
  apiBaseUrl: string;
  authUrl: string;
  appType?: AppType;
}

/** 全局配置接口 */
export interface GlobalConfig extends EnvConfig {
  currentEnv: Environment;
  isDevelopment: boolean;
  isTest: boolean;
  isProduction: boolean;
}

/** 域名解析结果 */
export interface DomainProfile {
  source: DomainSource;
  hostname: string;
  appType?: AppType;
  layoutType: LayoutType;
  tenantCode: string;
  displayName?: string;
  brandConfig: BrandConfig;
  tenantConfig: TenantConfig;
  envConfig: EnvConfig;
  isGenericSSO: boolean;
  requireManualTenantInput: boolean;
  matchedDomainName?: string;
}

// ============================================================================
// 统一租户配置
// ============================================================================

/**
 * 统一租户配置表
 *
 * 将品牌配置、域名配置、SSO 配置统一管理
 * 添加新租户时，只需在此处添加一个新的配置对象
 */
const TENANT_FULL_CONFIGS: Record<string, TenantFullConfig> = {
  // ========== BIBL 租户配置 ==========
  bibl: {
    tenantCode: "bibl",
    displayName: "BIBL",
    brand: {
      logo: "", // 将在运行时从 @ifa/ui 获取
      solidLogo: "", // 将在运行时从 @ifa/ui 获取
      name: "灣區雲服",
      systemName: "灣區雲服",
      favicon: "", // 将在运行时从 @ifa/ui 获取
      copyright: "© 2025 Bay Insurance Brokers Limited",
    },
    domains: [
      // 正式环境域名
      {
        pattern: "admin.cloud.bibl.com.hk",
        apiBaseUrl: "https://api.ifa.hk/api/v1",
        authUrl: "https://sso.cloud.bibl.com.hk/",
        appType: "admin",
      },
      {
        pattern: "cloud.bibl.com.hk",
        apiBaseUrl: "https://api.ifa.hk/api/v1",
        authUrl: "https://sso.cloud.bibl.com.hk/",
        appType: "tr",
      },
      {
        pattern: "h5.cloud.bibl.com.hk",
        apiBaseUrl: "https://api.ifa.hk/api/v1",
        authUrl: "https://sso.cloud.bibl.com.hk/",
        appType: "h5",
      },
      {
        pattern: "c.cloud.bibl.com.hk",
        apiBaseUrl: "https://api.ifa.hk/api/v1",
        authUrl: "https://sso.cloud.bibl.com.hk/",
        appType: "c",
      },
      {
        pattern: "sso.cloud.bibl.com.hk",
        apiBaseUrl: "https://api.ifa.hk/api/v1",
        authUrl: "https://sso.cloud.bibl.com.hk/",
      },
    ],
    sso: {
      hostname: "sso.cloud.bibl.com.hk",
      requireManualTenantInput: false,
    },
  },

  // ========== 默认租户配置（IFA） ==========
  default: {
    tenantCode: "default",
    displayName: "IFA",
    brand: {
      logo: "", // 将在运行时从 @ifa/ui 获取
      solidLogo: "", // 将在运行时从 @ifa/ui 获取
      name: "IFA",
      systemName: "Broker System",
      favicon: "", // 将在运行时从 @ifa/ui 获取
      copyright: "© 2025 BEEFINTECH",
    },
    domains: [
      // 正式环境域名（ifa.hk）
      {
        pattern: "admin.ifa.hk",
        apiBaseUrl: "https://api.ifa.hk/api/v1",
        authUrl: "https://sso.ifa.hk/",
        appType: "admin",
      },
      {
        pattern: "tr.ifa.hk",
        apiBaseUrl: "https://api.ifa.hk/api/v1",
        authUrl: "https://sso.ifa.hk/",
        appType: "tr",
      },
      {
        pattern: "h5.ifa.hk",
        apiBaseUrl: "https://api.ifa.hk/api/v1",
        authUrl: "https://sso.ifa.hk/",
        appType: "h5",
      },
      {
        pattern: "c.ifa.hk",
        apiBaseUrl: "https://api.ifa.hk/api/v1",
        authUrl: "https://sso.ifa.hk/",
        appType: "c",
      },
      {
        pattern: "sso.ifa.hk",
        apiBaseUrl: "https://api.ifa.hk/api/v1",
        authUrl: "https://sso.ifa.hk/",
      },

      // 开发/测试环境域名（ifa.beeplatform.net）
      {
        pattern: "web.ifa.beeplatform.net",
        apiBaseUrl: "https://api.ifa.beeplatform.net/api/v1",
        authUrl: "https://sso.ifa.beeplatform.net/",
        appType: "admin",
      },
      {
        pattern: "tr.ifa.beeplatform.net",
        apiBaseUrl: "https://api.ifa.beeplatform.net/api/v1",
        authUrl: "https://sso.ifa.beeplatform.net/",
        appType: "tr",
      },
      {
        pattern: "wap.ifa.beeplatform.net",
        apiBaseUrl: "https://api.ifa.beeplatform.net/api/v1",
        authUrl: "https://sso.ifa.beeplatform.net/",
        appType: "h5",
      },
      {
        pattern: "c.ifa.beeplatform.net",
        apiBaseUrl: "https://api.ifa.beeplatform.net/api/v1",
        authUrl: "https://sso.ifa.beeplatform.net/",
        appType: "c",
      },
      {
        pattern: "sso.ifa.beeplatform.net",
        apiBaseUrl: "https://api.ifa.beeplatform.net/api/v1",
        authUrl: "https://sso.ifa.beeplatform.net/",
      },

      // 本地开发环境（支持 localhost、127.0.0.1 和局域网 IP）
      {
        pattern: /^(localhost|127\.0\.0\.1|192\.168\.\d{1,3}\.\d{1,3}|10\.\d{1,3}\.\d{1,3}\.\d{1,3}|172\.(1[6-9]|2[0-9]|3[0-1])\.\d{1,3}\.\d{1,3}):5173$/,
        apiBaseUrl: import.meta.env.VITE_API_URL || "/api/v1",
        authUrl: import.meta.env.VITE_AUTH_URL || "http://localhost:5175/",
        appType: "tr",
        isLocal: true,
      },
      {
        pattern: /^(localhost|127\.0\.0\.1|192\.168\.\d{1,3}\.\d{1,3}|10\.\d{1,3}\.\d{1,3}\.\d{1,3}|172\.(1[6-9]|2[0-9]|3[0-1])\.\d{1,3}\.\d{1,3}):5174$/,
        apiBaseUrl: import.meta.env.VITE_API_URL || "/api/v1",
        authUrl: import.meta.env.VITE_AUTH_URL || "http://localhost:5175/",
        appType: "admin",
        isLocal: true,
      },
      {
        pattern: /^(localhost|127\.0\.0\.1|192\.168\.\d{1,3}\.\d{1,3}|10\.\d{1,3}\.\d{1,3}\.\d{1,3}|172\.(1[6-9]|2[0-9]|3[0-1])\.\d{1,3}\.\d{1,3}):5175$/,
        apiBaseUrl: import.meta.env.VITE_API_URL || "/api/v1",
        authUrl: import.meta.env.VITE_AUTH_URL || "http://localhost:5175/",
        isLocal: true,
      },
      {
        pattern: /^(localhost|127\.0\.0\.1|192\.168\.\d{1,3}\.\d{1,3}|10\.\d{1,3}\.\d{1,3}\.\d{1,3}|172\.(1[6-9]|2[0-9]|3[0-1])\.\d{1,3}\.\d{1,3}):5177$/,
        apiBaseUrl: import.meta.env.VITE_API_URL || "/api/v1",
        authUrl: import.meta.env.VITE_AUTH_URL || "http://localhost:5175/",
        appType: "h5",
        isLocal: true,
      },
      {
        pattern: /^(localhost|127\.0\.0\.1|192\.168\.\d{1,3}\.\d{1,3}|10\.\d{1,3}\.\d{1,3}\.\d{1,3}|172\.(1[6-9]|2[0-9]|3[0-1])\.\d{1,3}\.\d{1,3})/,
        apiBaseUrl: import.meta.env.VITE_API_URL || "/api/v1",
        authUrl: import.meta.env.VITE_AUTH_URL || "http://localhost:5175/",
        isLocal: true,
      },
    ],
    sso: {
      hostname: "sso.ifa.beeplatform.net",
      requireManualTenantInput: true,
    },
  },
};

// ============================================================================
// 从统一配置生成各个子配置
// ============================================================================

/** 域名配置列表（扁平化，用于域名匹配） */
const DOMAIN_CONFIGS: Array<DomainConfig & { tenantCode: string; name: string }> = [];

/** 品牌配置映射表 */
const BRAND_CONFIGS: Record<string, BrandConfig> = {};

/** 租户配置列表 */
export const TENANT_CONFIGS: TenantConfig[] = [];

/** SSO 通用域名列表 */
export const SSO_GENERIC_HOSTNAMES: string[] = [];

// 生成各个子配置
Object.entries(TENANT_FULL_CONFIGS).forEach(([key, config]) => {
  // 生成品牌配置
  BRAND_CONFIGS[config.tenantCode] = config.brand;

  // 生成租户配置
  TENANT_CONFIGS.push({
    hostname: config.sso.hostname,
    tenantCode: config.tenantCode,
    requireManualTenantInput: config.sso.requireManualTenantInput,
    displayName: config.displayName,
  });

  // 生成域名配置（扁平化）
  config.domains.forEach((domain, index) => {
    DOMAIN_CONFIGS.push({
      ...domain,
      tenantCode: config.tenantCode,
      name: `${config.displayName} - ${domain.appType || "SSO"} (${index + 1})`,
    });
  });

  // 生成 SSO 通用域名列表
  if (config.sso.requireManualTenantInput) {
    SSO_GENERIC_HOSTNAMES.push(config.sso.hostname);
  }
});

// 添加 localhost 到 SSO 通用域名（用于本地开发测试）
SSO_GENERIC_HOSTNAMES.push("localhost");

const DEFAULT_TENANT_CODE = "default";

// ============================================================================
// 默认配置
// ============================================================================

/** 默认配置（当没有匹配到任何域名时使用） */
const DEFAULT_CONFIG: EnvConfig = {
  apiBaseUrl: import.meta.env.VITE_API_URL || "https://api.ifa.hk/api/v1",
  authUrl: import.meta.env.VITE_AUTH_URL || "https://sso.ifa.hk/",
};

/** 服务端渲染或构建时的默认配置 */
const SSR_DEFAULT_CONFIG: EnvConfig = {
  apiBaseUrl: import.meta.env.VITE_API_URL || "/api/v1",
  authUrl: import.meta.env.VITE_AUTH_URL || "https://sso.ifa.hk/",
};

// ============================================================================
// 工具函数
// ============================================================================

const getTenantFullConfig = (tenantCode?: string): TenantFullConfig => {
  if (tenantCode && tenantCode in TENANT_FULL_CONFIGS) {
    return TENANT_FULL_CONFIGS[tenantCode];
  }
  return TENANT_FULL_CONFIGS[DEFAULT_TENANT_CODE];
};

const getDefaultEnvConfig = (): EnvConfig => {
  if (typeof window === "undefined") {
    return SSR_DEFAULT_CONFIG;
  }
  return DEFAULT_CONFIG;
};

const normalizeHostname = (hostname: string): string => hostname.trim().toLowerCase();

const decodeValue = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const parseHostnameFromInput = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return "";

  const tryParse = (candidate: string): string | null => {
    try {
      return new URL(candidate).host;
    } catch {
      return null;
    }
  };

  const direct = tryParse(trimmed);
  if (direct) return direct;

  const decoded = decodeValue(trimmed);
  if (decoded !== trimmed) {
    const decodedParsed = tryParse(decoded);
    if (decodedParsed) return decodedParsed;
  }

  if (trimmed.includes("/") || trimmed.includes("?")) {
    const withScheme = tryParse(`http://${trimmed}`);
    if (withScheme) return withScheme;
  }

  if (decoded !== trimmed && (decoded.includes("/") || decoded.includes("?"))) {
    const withScheme = tryParse(`http://${decoded}`);
    if (withScheme) return withScheme;
  }

  return trimmed;
};

/**
 * AppType 到 LayoutType 的映射
 */
const APP_TYPE_TO_LAYOUT: Record<AppType, LayoutType> = {
  admin: "web",
  tr: "pad",
  h5: "mobile",
  c: "mobile",
};

const getLayoutTypeByAppType = (appType?: AppType): LayoutType => {
  if (appType) {
    return APP_TYPE_TO_LAYOUT[appType];
  }
  return "web";
};

const buildTenantConfig = (tenantCode?: string): TenantConfig => {
  const fullConfig = getTenantFullConfig(tenantCode);
  return {
    hostname: fullConfig.sso.hostname,
    tenantCode: fullConfig.tenantCode,
    requireManualTenantInput: fullConfig.sso.requireManualTenantInput,
    displayName: fullConfig.displayName,
  };
};

/**
 * 匹配域名配置
 *
 * 支持两种匹配模式：
 * 1. 字符串模式：精确匹配（如 'admin.ifa.hk' 只匹配 admin.ifa.hk）
 * 2. 正则表达式模式：使用正则表达式进行匹配（如 /^localhost/ 匹配所有以 localhost 开头的域名）
 *
 * @param hostname 主机名（包含端口号）
 * @returns 匹配的配置，如果没有匹配则返回 null
 */
const matchDomainConfig = (
  hostname: string
): (DomainConfig & { tenantCode: string }) | null => {
  const lowerHostname = hostname.toLowerCase();

  for (const config of DOMAIN_CONFIGS) {
    if (typeof config.pattern === "string") {
      // 字符串精确匹配
      if (lowerHostname === config.pattern) {
        return config;
      }
    } else {
      // 正则表达式匹配
      if (config.pattern.test(lowerHostname)) {
        return config;
      }
    }
  }

  return null;
};

/**
 * 统一解析域名配置（支持当前域名或 callback URL）
 */
export function resolveDomainProfile(input: {
  source: DomainSource;
  value: string;
}): DomainProfile {
  const rawHostname = parseHostnameFromInput(input.value);
  const hostname = normalizeHostname(rawHostname);
  const hostnameWithoutPort = hostname.split(":")[0];

  const matchedConfig = hostname ? matchDomainConfig(hostname) : null;
  const tenantCode = matchedConfig?.tenantCode || DEFAULT_TENANT_CODE;
  const tenantConfig = buildTenantConfig(tenantCode);
  const brandConfig = getTenantFullConfig(tenantConfig.tenantCode).brand;

  const envConfig: EnvConfig = matchedConfig
    ? {
        apiBaseUrl: matchedConfig.apiBaseUrl,
        authUrl: matchedConfig.authUrl,
        ...(matchedConfig.appType ? { appType: matchedConfig.appType } : {}),
      }
    : getDefaultEnvConfig();

  const appType = matchedConfig?.appType;
  const layoutType = getLayoutTypeByAppType(appType);
  const isGenericSSO = hostnameWithoutPort ? isGenericSSODomain(hostnameWithoutPort) : false;
  const requireManualTenantInput =
    isGenericSSO || tenantConfig.requireManualTenantInput === true;

  return {
    source: input.source,
    hostname,
    appType,
    layoutType,
    tenantCode: tenantConfig.tenantCode,
    displayName: tenantConfig.displayName,
    brandConfig,
    tenantConfig,
    envConfig,
    isGenericSSO,
    requireManualTenantInput,
    matchedDomainName: matchedConfig?.name,
  };
}

/**
 * 根据环境变量自动确定当前环境
 */
const getEnv = (): Environment => {
  // 优先使用 Vite 提供的 MODE
  if (import.meta.env.MODE) {
    if (import.meta.env.DEV) return "development";

    const mode = import.meta.env.MODE;
    if (mode === "production") return "production";
    if (mode === "test") return "test";
    if (mode === "development") return "development";
  }

  // 运行时环境检测（作为后备）
  if (typeof window !== "undefined") {
    const host = window.location.host;
    // 支持 localhost、127.0.0.1 和局域网 IP（192.168.x.x、10.x.x.x、172.16-31.x.x）
    if (
      host.includes("localhost") ||
      host.includes("127.0.0.1") ||
      /^(192\.168\.|10\.|172\.(1[6-9]|2[0-9]|3[0-1])\.)/.test(host)
    ) {
      return "development";
    }
    if (host.includes("uat") || host.includes("test")) {
      return "test";
    }
  }

  return "production";
};

/**
 * 获取基于域名的配置
 */
const getDomainConfig = (): EnvConfig => {
  // 服务端渲染或构建时的默认配置
  if (typeof window === "undefined") {
    return SSR_DEFAULT_CONFIG;
  }

  // 获取主机名（包含端口号）
  const hostname = window.location.host;

  const profile = resolveDomainProfile({ source: "domain", value: hostname });

  if (profile.matchedDomainName) {
    console.log(
      `[Config] 匹配到配置: ${profile.matchedDomainName} (租户: ${profile.tenantCode})`
    );
    return profile.envConfig;
  }

  // 没有匹配到，使用默认配置
  console.warn(`[Config] 未匹配到域名配置，使用默认配置: ${hostname}`);
  return profile.envConfig;
};

// ============================================================================
// 导出配置
// ============================================================================

const currentEnv = getEnv();

/** 全局配置对象 */
export const config: GlobalConfig = {
  ...getDomainConfig(),
  currentEnv,
  isDevelopment: currentEnv === "development",
  isTest: currentEnv === "test",
  isProduction: currentEnv === "production",
};

// 开发环境下打印配置信息
if (config.isDevelopment && typeof window !== "undefined") {
  console.log("[Config] 当前配置:", config);
}

// ============================================================================
// SSO 登录页布局类型映射
// ============================================================================

/**
 * 根据 callback URL 获取 SSO 登录页应该使用的布局类型
 *
 * 匹配逻辑：
 * 1. 从 callback URL 的 hostname 匹配 DOMAIN_CONFIGS
 * 2. 如果匹配到配置且有 appType，使用配置中的 appType 映射到 layoutType
 * 3. 默认返回 web 布局
 *
 * @param callbackUrl - SSO 回调 URL
 * @returns 布局类型（web | pad | mobile）
 *
 * @example
 * // 生产环境
 * getLayoutTypeFromCallback("https://tr.ifa.hk") // => "pad"
 * getLayoutTypeFromCallback("https://admin.ifa.hk") // => "web"
 *
 * // 本地开发（根据端口号）
 * getLayoutTypeFromCallback("http://localhost:5173") // => "pad"
 * getLayoutTypeFromCallback("http://localhost:5174") // => "web"
 * getLayoutTypeFromCallback("http://localhost:5176") // => "mobile"
 */
export const getLayoutTypeFromCallback = (callbackUrl: string): LayoutType => {
  const profile = resolveDomainProfile({ source: "callback", value: callbackUrl });

  if (profile.appType) {
    console.log(
      `[Config] 从域名配置获取布局类型: ${profile.matchedDomainName || profile.hostname} -> ${profile.appType} -> ${profile.layoutType}`
    );
    return profile.layoutType;
  }

  console.log(`[Config] 未匹配到配置，使用默认布局: web (callback: ${callbackUrl})`);
  return "web";
};

/**
 * 根据租户码获取品牌配置
 *
 * @param tenantCode - 租户码（如 "bibl"）
 * @returns 品牌配置对象
 *
 * @example
 * const brandConfig = getBrandConfig("bibl");
 * // => { logo: "...", name: "灣區雲服", systemName: "灣區雲服", favicon: "...", copyright: "..." }
 *
 * const defaultConfig = getBrandConfig();
 * // => { logo: "...", name: "灣區雲服", systemName: "灣區雲服", favicon: "...", copyright: "..." }
 */
export const getBrandConfig = (tenantCode?: string): BrandConfig => {
  if (tenantCode && tenantCode in BRAND_CONFIGS) {
    return BRAND_CONFIGS[tenantCode];
  }
  return BRAND_CONFIGS.default;
};

/**
 * 根据域名获取租户配置
 *
 * @param hostname - 域名（如 "sso.cloud.bibl.com.hk"）
 * @returns 租户配置对象，如果未找到则返回 null
 *
 * @example
 * const config = getTenantConfigByHostname("sso.cloud.bibl.com.hk");
 * // => { hostname: "sso.cloud.bibl.com.hk", tenantCode: "bibl", ... }
 */
export function getTenantConfigByHostname(hostname: string): TenantConfig | null {
  const target = hostname.toLowerCase();
  return TENANT_CONFIGS.find((config) => config.hostname.toLowerCase() === target) || null;
}

/**
 * 判断域名是否为 SSO 通用域名
 *
 * @param hostname - 域名
 * @returns 是否为通用 SSO 域名
 *
 * @example
 * isGenericSSODomain("sso.ifa.beeplatform.net") // => true
 * isGenericSSODomain("sso.cloud.bibl.com.hk") // => false
 */
export function isGenericSSODomain(hostname: string): boolean {
  const target = hostname.toLowerCase();
  return SSO_GENERIC_HOSTNAMES.some((item) => item.toLowerCase() === target);
}

/**
 * 获取当前环境的租户配置信息
 *
 * @returns 包含租户配置、是否为通用 SSO、是否需要手动输入租户码的对象
 *
 * @example
 * const { config, isGenericSSO, requireManualTenantInput } = getCurrentTenantConfig();
 * // config: TenantConfig | null
 * // isGenericSSO: boolean
 * // requireManualTenantInput: boolean
 */
export function getCurrentTenantConfig(): {
  config: TenantConfig | null;
  isGenericSSO: boolean;
  requireManualTenantInput: boolean;
} {
  const hostname = typeof window !== "undefined" ? window.location.host : "";
  const profile = resolveDomainProfile({ source: "domain", value: hostname });

  return {
    config: profile.tenantConfig,
    isGenericSSO: profile.isGenericSSO,
    requireManualTenantInput: profile.requireManualTenantInput,
  };
}
